import { useState } from "react"
import { Button } from "@/components/ui/button"
import { AnimatedCard } from "./AnimatedCard"
import { useApexState } from "@/hooks/useApexState"
import { 
  Plus, 
  ShoppingBag, 
  Camera,
  Heart,
  MessageSquare,
  X,
  Zap
} from "lucide-react"

interface QuickAction {
  id: string
  label: string
  icon: React.ElementType
  color: string
  action: () => void
}

interface QuickActionsProps {
  className?: string
}

export function QuickActions({ className }: QuickActionsProps) {
  const { state, actions } = useApexState()
  const [isExpanded, setIsExpanded] = useState(false)

  const quickActions: QuickAction[] = [
    {
      id: "add-movement",
      label: "Log Movement",
      icon: Zap,
      color: "bg-accent hover:bg-accent/90 text-accent-foreground",
      action: () => {
        // Navigate to movement logging or trigger quick add
        actions.setActiveTab('move')
        setIsExpanded(false)
      }
    },
    {
      id: "join-challenge",
      label: "Join Challenge",
      icon: Camera,
      color: "bg-secondary hover:bg-secondary/90 text-secondary-foreground",
      action: () => {
        // Navigate to active challenge
        actions.setActiveTab('studio')
        setIsExpanded(false)
      }
    },
    {
      id: "wishlist",
      label: "Wishlist",
      icon: Heart,
      color: "bg-primary hover:bg-primary/90 text-primary-foreground",
      action: () => {
        // Open wishlist modal
        console.log("Open wishlist")
        setIsExpanded(false)
      }
    },
    {
      id: "cart",
      label: "Cart",
      icon: ShoppingBag,
      color: "bg-muted hover:bg-muted/90 text-muted-foreground",
      action: () => {
        // Open cart
        console.log("Open cart")
        setIsExpanded(false)
      }
    },
    {
      id: "concierge",
      label: "Concierge",
      icon: MessageSquare,
      color: "bg-tier-architect hover:bg-tier-architect/90 text-black",
      action: () => {
        // Open concierge chat (if user has access)
        if (state.user?.tier === 'architect') {
          console.log("Open concierge chat")
        } else {
          console.log("Show upgrade prompt")
        }
        setIsExpanded(false)
      }
    }
  ]

  return (
    <div className={`fixed bottom-24 right-4 z-40 ${className}`}>
      <div className="flex flex-col items-end gap-3">
        {/* Action Buttons */}
        {isExpanded && (
          <div className="flex flex-col gap-2 animate-fade-in">
            {quickActions.map((action, index) => {
              const Icon = action.icon
              return (
                <AnimatedCard key={action.id} className={`delay-${index * 50}`}>
                  <div className="flex items-center gap-3">
                    <div className="bg-background/90 backdrop-blur-sm px-3 py-2 rounded-lg border border-border">
                      <span className="text-sm font-medium text-foreground whitespace-nowrap">
                        {action.label}
                      </span>
                    </div>
                    <Button
                      onClick={action.action}
                      className={`w-12 h-12 rounded-full p-0 ${action.color} shadow-lg`}
                    >
                      <Icon size={20} />
                    </Button>
                  </div>
                </AnimatedCard>
              )
            })}
          </div>
        )}

        {/* Main Toggle Button */}
        <AnimatedCard>
          <Button
            onClick={() => setIsExpanded(!isExpanded)}
            className={`
              w-14 h-14 rounded-full p-0 shadow-lg transition-all duration-300
              ${isExpanded 
                ? 'bg-destructive hover:bg-destructive/90 text-destructive-foreground rotate-45' 
                : 'bg-primary hover:bg-primary/90 text-primary-foreground'
              }
            `}
          >
            {isExpanded ? (
              <X size={24} className="transition-transform duration-300" />
            ) : (
              <Plus size={24} className="transition-transform duration-300" />
            )}
          </Button>
        </AnimatedCard>
      </div>

      {/* Overlay */}
      {isExpanded && (
        <div 
          className="fixed inset-0 z-30 bg-black/20 backdrop-blur-[1px]"
          onClick={() => setIsExpanded(false)}
        />
      )}
    </div>
  )
}

interface QuickActionTooltipProps {
  action: QuickAction
  children: React.ReactNode
}

export function QuickActionTooltip({ action, children }: QuickActionTooltipProps) {
  const [isVisible, setIsVisible] = useState(false)

  return (
    <div 
      className="relative"
      onMouseEnter={() => setIsVisible(true)}
      onMouseLeave={() => setIsVisible(false)}
    >
      {children}
      {isVisible && (
        <div className="absolute right-full mr-2 top-1/2 transform -translate-y-1/2">
          <div className="bg-background/90 backdrop-blur-sm px-3 py-2 rounded-lg border border-border shadow-lg">
            <span className="text-sm font-medium text-foreground whitespace-nowrap">
              {action.label}
            </span>
          </div>
        </div>
      )}
    </div>
  )
}